import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Avatar, IconButton, InputBase, Tooltip,
  useTheme, useMediaQuery, Tabs, Tab, Divider, Badge, Button,
  Menu, MenuItem, LinearProgress, Chip, Drawer
} from '@mui/material';
import {
  Search as SearchIcon, MoreVert as MoreVertIcon,
  SmartToy as BotIcon, HeadsetMic as HeadsetMicIcon,
  HourglassEmpty as HourglassIcon, Add as AddIcon,
  CheckCircle as CheckCircleIcon, KeyboardArrowDown as ArrowDownIcon,
  Tune as TuneIcon, WhatsApp as WhatsAppIcon,
  HeadsetMic as HeadsetIcon, Warning as WarningIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { atendimentoService, AtendimentoTicket } from '../../services/atendimentoService';
import { useAuth, usePermissions } from '../../contexts/AuthContext';
import { useAtendimentoNotification } from '../../contexts/AtendimentoNotificationContext';
import { isAtendimentoTicketVisivel, ticketFoiAssumidoPorOutroEnquantoAguardava, deveIgnorarAtualizacaoTicketAtivo, mensagemNaoReabreTicketFechado, isAtendimentoTicketFechado } from '../../utils/atendimentoTicketUtils';
import { toast } from 'react-toastify';
import ChatColuna from './componentes/ChatColuna';
import ContatoDetalhes from './componentes/ContatoDetalhes';
import NovoAtendimentoDialog from './componentes/NovoAtendimentoDialog';
import AtendimentosFechadosDialog from './componentes/AtendimentosFechadosDialog';
import ClientDetailsModal from '../../components/modals/ClientDetailsModal';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { isCopaTheme, isUsaTheme } from '../../themes/themeRegistry';
import fundoHeroCopa from '../../assets/fundo_hero_copa.png';
import fundoChat from '../../assets/fundo_chat.png';
import fundoChatTemaAzul from '../../assets/fundo_chat_tema_azul.png';

const CORES = ['#5c6bc0', '#26a69a', '#ef5350', '#ff7043', '#ab47bc', '#29b6f6', '#66bb6a', '#ffa726'];

const getInitials = (name: string) => name ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() : '??';

const normalizeHexColor = (color?: string | null) => {
  if (!color) return '#1565c0';
  return color.startsWith('#') ? color : `#${color}`;
};

const fmtTime = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  const isToday = d.toDateString() === new Date().toDateString();
  return isToday
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const formatConnectionType = (tipo?: string) => {
  if (tipo === 'whatsapp') return 'WhatsApp';
  if (tipo === 'instagram') return 'Instagram';
  return tipo || 'Conexao';
};

const getClienteStatus = (ticket: AtendimentoTicket & { cliente?: { status?: string | null } }) => {
  return ticket.contato?.cliente?.status?.trim().toLowerCase()
    || ticket.cliente?.status?.trim().toLowerCase()
    || '';
};

const isClienteStatusCritico = (status: string) => {
  return ['inativo', 'suspenso', 'bloqueado', 'inadimplente'].includes(status);
};

const formatarUltimaMensagem = (msg?: string | null) => {
  if (!msg) return 'Nova conexão';
  return msg
    .replace(/\[sticker\]/gi, '[figurinha]')
    .replace(/\[document\]/gi, '[documento]')
    .replace(/\[location\]/gi, '[localização]')
    .replace(/\[contact\]/gi, '[contato]');
};

const ConversaItem: React.FC<{
  at: AtendimentoTicket;
  selecionado: boolean;
  onClick: () => void;
}> = ({ at, selecionado, onClick }) => {
  const theme = useTheme();
  const corAvatar = CORES[(at.contato_id || at.id || 0) % CORES.length];
  const corSetor = normalizeHexColor(at.setor?.cor);
  const statusCliente = getClienteStatus(at);
  const mostrarStatusCliente = Boolean(at.contato?.cliente_id && isClienteStatusCritico(statusCliente));
  const nomeContato = at.contato?.nome || at.contato?.numero || 'Sem Nome';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.2, px: 1.5, py: 1.2,
        cursor: 'pointer',
        borderLeft: selecionado ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
        bgcolor: selecionado ? 'rgba(1,81,196,0.07)' : 'transparent',
        transformOrigin: 'center',
        '&:hover': {
          bgcolor: selecionado ? 'rgba(1,81,196,0.1)' : 'action.hover',
          transform: 'scale(1.025)',
          boxShadow: theme.shadows[2],
          zIndex: 1,
        },
        transition: 'background-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease',
      }}
    >
      <Badge badgeContent={at.msgs_nao_lidas || undefined} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14, top: 2, right: 2 } }}>
        <Avatar src={atendimentoService.getAvatarUrl(at.contato?.foto_perfil)} sx={{ bgcolor: corAvatar, width: 40, height: 40, fontSize: '0.8rem', flexShrink: 0 }}>
          {!at.contato?.foto_perfil && getInitials(nomeContato)}
        </Avatar>
      </Badge>

      <Box flex={1} minWidth={0}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
            {at.contato?.cliente_id && (
              <Tooltip title="Contato vinculado a um cliente">
                <CheckCircleIcon sx={{ fontSize: 14, color: '#2e7d32', flexShrink: 0 }} />
              </Tooltip>
            )}
            {mostrarStatusCliente && (
              <Tooltip title={`Atenção: Cliente está com status ${statusCliente.toUpperCase()}`}>
                <WarningIcon
                  sx={{
                    fontSize: 14,
                    color: statusCliente === 'inativo'
                      ? '#9e9e9e'
                      : statusCliente === 'bloqueado'
                        ? '#ed6c02'
                        : '#d32f2f',
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
            <Typography variant="body2" fontWeight={at.msgs_nao_lidas ? 700 : 500} noWrap sx={{ minWidth: 0 }}>
              {nomeContato}
            </Typography>


          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, maxWidth: 100 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.1 }}>
              {at.ultima_msg_at && fmtTime(at.ultima_msg_at)}
            </Typography>
          </Box>
        </Box>



        <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.2}>
          <Typography variant="caption" color={at.msgs_nao_lidas ? "text.primary" : "text.secondary"} fontWeight={at.msgs_nao_lidas ? 600 : 400} noWrap sx={{ maxWidth: 180, fontSize: '0.7rem', flex: 1 }}>
            {formatarUltimaMensagem(at.ultima_mensagem)}
          </Typography>
          {at.msgs_nao_lidas === 0 && <CheckCircleIcon sx={{ fontSize: 13, color: '#25d366', flexShrink: 0, ml: 0.5 }} />}
        </Box>
        {(at.setor || at.conexao || at.usuario) && (
          <Box display="flex" mt={0.45} alignItems="center" justifyContent="space-between" gap={0.5}>
            <Box display="flex" gap={0.5} minWidth={0} flex={1} flexWrap="wrap">
              {at.setor && (
                <Chip
                  label={at.setor.nome}
                  size="small"
                  sx={{
                    height: 18,
                    maxWidth: 95,
                    bgcolor: corSetor,
                    color: '#fff',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    '& .MuiChip-label': {
                      px: 0.8,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              )}
              {at.conexao && (
                <Chip
                  icon={<WhatsAppIcon sx={{ fontSize: '0.78rem !important' }} />}
                  label={at.conexao.nome}
                  title={`${formatConnectionType(at.conexao.tipo)}${at.conexao.numero ? ` - ${at.conexao.numero}` : ''}`}
                  size="small"
                  sx={{
                    height: 18,
                    maxWidth: 105,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(37,211,102,0.16)' : 'rgba(37,211,102,0.12)',
                    color: theme.palette.mode === 'dark' ? '#8ee6ab' : '#128c4a',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    '& .MuiChip-icon': {
                      ml: 0.6,
                      color: 'inherit',
                    },
                    '& .MuiChip-label': {
                      px: 0.7,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              )}
            </Box>
            {at.usuario && (

              <Typography
                variant="caption"
                color="primary.main"
                fontWeight={700}
                noWrap
                title={at.usuario.nome}
                sx={{ maxWidth: 85, fontSize: '0.65rem', lineHeight: 1.2, flexShrink: 0, textAlign: 'right' }}
              >
                <HeadsetIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0, ml: 0.5 }} /> {at.usuario.nome}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const AtendimentosPage: React.FC = () => {
  const theme = useTheme();
  const { themeId } = useAppTheme();
  const isCopa = isCopaTheme(themeId);
  const isUsa = isUsaTheme(themeId);
  const emptyStateBackground = isCopa
    ? fundoHeroCopa
    : isUsa
      ? fundoChatTemaAzul
      : fundoChat;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { user } = useAuth();
  const { can } = usePermissions();
  const { socket, on, setOpenTicketId, marcarTicketComoLidoLocal } = useAtendimentoNotification();
  const podeVerTicketsSemSetor = can('atendimento.ver_tickets_sem_setor');
  const podeVerTicketsOutrosUsuarios = can('atendimento.ver_tickets_outros_usuarios');
  const podeCriarNovoTicket = can('atendimento.criar_novo_ticket');
  const podeVerTicketsFechados = can('atendimento.ver_tickets_fechados');
  const podeExibirTicketsBot = can('atendimento.exibir_tickets_bot');

  const [tickets, setTickets] = useState<AtendimentoTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabAtiva, setTabAtiva] = useState<string>('em_atendimento');
  const [selecionado, setSelecionado] = useState<AtendimentoTicket | null>(null);
  const [busca, setBusca] = useState('');
  const [exibirTodos, setExibirTodos] = useState(false);
  const [novoAtendimentoAberto, setNovoAtendimentoAberto] = useState(false);
  const [fechadosAberto, setFechadosAberto] = useState(false);
  const [mostrarLista, setMostrarLista] = useState(true);
  const [mostrarDetalhes, setMostrarDetalhes] = useState(!isTablet && !isMobile);
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [drawerContainer, setDrawerContainer] = useState<HTMLElement | null>(null);
  const [perfilClienteModals, setPerfilClienteModals] = useState<Array<{
    instanceId: string;
    clientId: number;
    clientName?: string;
    minimized: boolean;
  }>>([]);

  const handleOpenClientProfile = (clientId: number, clientName?: string) => {
    const instanceId = `${clientId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setPerfilClienteModals((prev) => [
      ...prev.map((modal) => ({ ...modal, minimized: true })),
      { instanceId, clientId, clientName, minimized: false },
    ]);
  };

  const handleCloseClientProfile = (instanceId: string) => {
    setPerfilClienteModals((prev) => prev.filter((modal) => modal.instanceId !== instanceId));
  };

  const handleClientProfileMinimizedChange = (instanceId: string, minimized: boolean) => {
    setPerfilClienteModals((prev) => {
      if (!minimized) {
        return prev.map((modal) => ({
          ...modal,
          minimized: modal.instanceId !== instanceId,
        }));
      }
      return prev.map((modal) =>
        modal.instanceId === instanceId ? { ...modal, minimized: true } : modal,
      );
    });
  };

  useEffect(() => {
    if (isMobile) {
      setMostrarDetalhes(false);
    } else if (!isTablet) {
      setMostrarDetalhes(true);
    }
  }, [isMobile, isTablet]);

  const location = useLocation();
  const ticketInicialRef = useRef<number | null>((location.state as any)?.ticketId || null);
  const selecionadoIdRef = useRef<number | null>(null);
  const userIdRef = useRef<number | null>(user?.id ? Number(user.id) : null);
  const podeVerTicketsSemSetorRef = useRef(podeVerTicketsSemSetor);
  const podeVerTicketsOutrosUsuariosRef = useRef(podeVerTicketsOutrosUsuarios);
  const podeExibirTicketsBotRef = useRef(podeExibirTicketsBot);

  useEffect(() => {
    selecionadoIdRef.current = selecionado?.id || null;
    setOpenTicketId(selecionado?.id || null);
  }, [selecionado?.id, setOpenTicketId]);

  useEffect(() => () => setOpenTicketId(null), [setOpenTicketId]);

  useEffect(() => {
    userIdRef.current = user?.id ? Number(user.id) : null;
  }, [user?.id]);

  useEffect(() => {
    podeVerTicketsSemSetorRef.current = podeVerTicketsSemSetor;
  }, [podeVerTicketsSemSetor]);

  useEffect(() => {
    podeVerTicketsOutrosUsuariosRef.current = podeVerTicketsOutrosUsuarios;
  }, [podeVerTicketsOutrosUsuarios]);

  useEffect(() => {
    podeExibirTicketsBotRef.current = podeExibirTicketsBot;
  }, [podeExibirTicketsBot]);

  useEffect(() => {
    if (!podeVerTicketsOutrosUsuarios) {
      setExibirTodos(false);
    }
  }, [podeVerTicketsOutrosUsuarios]);

  useEffect(() => {
    if (!podeExibirTicketsBot && tabAtiva === 'bot') {
      setTabAtiva('em_atendimento');
      setSelecionado(null);
    }
  }, [podeExibirTicketsBot, tabAtiva]);

  const ticketVisivelNoSocket = (ticket: AtendimentoTicket) => isAtendimentoTicketVisivel(ticket, {
    userId: userIdRef.current,
    podeVerTicketsSemSetor: podeVerTicketsSemSetorRef.current,
    podeVerTicketsOutrosUsuarios: podeVerTicketsOutrosUsuariosRef.current,
    podeExibirTicketsBot: podeExibirTicketsBotRef.current,
  });

  const handleNovoTicket = (ticket: AtendimentoTicket) => {
    aplicarTicketAtualizado(ticket);
  };

  // [Atendimento] Fecha chat quando outro atendente assume o ticket que estava aberto.
  const fecharChatTicket = (ticket?: AtendimentoTicket) => {
    setSelecionado(null);
    selecionadoIdRef.current = null;
    setMostrarLista(true);

    if (ticket) {
      toast.info(`Este atendimento já está sendo realizado por ${ticket.usuario?.nome || 'outro atendente'}.`);
    }
  };

  const aplicarTicketAtualizado = (ticket: AtendimentoTicket) => {
    const visivel = ticketVisivelNoSocket(ticket);
    const eraSelecionado = selecionadoIdRef.current === ticket.id;
    let ticketAnterior: AtendimentoTicket | undefined;

    setTickets((prev) => {
      ticketAnterior = prev.find((t) => t.id === ticket.id);
      const exists = prev.some((t) => t.id === ticket.id);
      if (!visivel) {
        return prev.filter((t) => t.id !== ticket.id);
      }
      if (deveIgnorarAtualizacaoTicketAtivo(ticket, { ticketJaNaLista: exists, ticketAberto: eraSelecionado })) {
        // reaction/sticker em ticket fechado: mensagem e salva no backend, mas nao entra na fila.
        return prev;
      }
      return exists
        ? prev.map((t) => (t.id === ticket.id ? ticket : t))
        : [ticket, ...prev];
    });

    // Fecha chat apenas se estava aguardando e outro assumiu (admins podem ver tickets de outros).
    const foiAssumidoPorOutro = ticketFoiAssumidoPorOutroEnquantoAguardava(
      ticket,
      ticketAnterior,
      userIdRef.current,
    );

    if (eraSelecionado && (foiAssumidoPorOutro || !visivel)) {
      fecharChatTicket(foiAssumidoPorOutro ? ticket : undefined);
      return;
    }

    setSelecionado((prev) => (prev?.id === ticket.id ? ticket : prev));
  };

  const carregarTickets = async () => {
    try {
      setLoading(true);
      const data = await atendimentoService.getTickets();
      setTickets(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarTickets();
  }, []);

  useEffect(() => {
    if (!socket) return;

    const unsubNovoTicket = on('atendimento:novo_ticket', handleNovoTicket);
    const unsubTicketCreate = on('atendimento:ticket_create', handleNovoTicket);
    const unsubTicketAtualizado = on('atendimento:ticket_atualizado', aplicarTicketAtualizado);
    const unsubTicketUpdate = on('atendimento:ticket_update', aplicarTicketAtualizado);
    const unsubTicketDelete = on('atendimento:ticket_delete', (data: { ticketId?: number; id?: number }) => {
      const ticketId = data?.ticketId || data?.id;
      if (!ticketId) return;
      setTickets(prev => prev.filter(t => t.id !== ticketId));
      setSelecionado(prev => prev?.id === ticketId ? null : prev);
      if (selecionadoIdRef.current === ticketId) {
        selecionadoIdRef.current = null;
        setMostrarLista(true);
      }
    });
    const unsubNovaMensagem = on('atendimento:nova_mensagem', (data: any) => {
      if (!data?.ticket?.id) return;

      const ticketAberto = selecionadoIdRef.current === data.ticket.id;

      // reaction/sticker em ticket fechado: atualiza historico se chat aberto; senao ignora fila.
      if (
        mensagemNaoReabreTicketFechado(data.mensagem?.tipo)
        && isAtendimentoTicketFechado(data.ticket)
        && !ticketAberto
      ) {
        return;
      }

      if (ticketAberto) {
        const ticketLido = { ...data.ticket, msgs_nao_lidas: 0 };
        aplicarTicketAtualizado(ticketLido);
        marcarTicketComoLidoLocal(data.ticket.id);
        if (!mensagemNaoReabreTicketFechado(data.mensagem?.tipo)) {
          atendimentoService.marcarTicketComoLido(data.ticket.id).catch((err) => {
            console.error('Erro ao marcar atendimento como lido', err);
          });
        }
        return;
      }

      aplicarTicketAtualizado(data.ticket);
    });

    return () => {
      unsubNovoTicket();
      unsubTicketCreate();
      unsubTicketAtualizado();
      unsubTicketUpdate();
      unsubTicketDelete();
      unsubNovaMensagem();
    };
  }, [socket, on, marcarTicketComoLidoLocal]);

  useEffect(() => {
    if (!ticketInicialRef.current || tickets.length === 0) return;

    const ticket = tickets.find((t) => t.id === ticketInicialRef.current);
    if (!ticket) return;

    setSelecionado(ticket);
    setTabAtiva(ticket.status === 'fechado_bot' ? 'bot' : ticket.status);
    if (isMobile) setMostrarLista(false);
    ticketInicialRef.current = null;
  }, [tickets, isMobile]);

  const contadorTab = (s: string) => {
    if (s === 'bot') return tickets.filter(t => t.is_bot && t.status !== 'fechado').length;
    return tickets.filter(t => {
      const isAtendendoDeOutroUsuario = s === 'em_atendimento'
        && podeVerTicketsOutrosUsuarios
        && !exibirTodos
        && user?.id
        && t.usuario_id !== user.id;
      return t.status === s && !t.is_bot && !isAtendendoDeOutroUsuario;
    }).length;
  };

  const filtrados = tickets.filter(a => {
    if (!a) return false;
    const isTab = tabAtiva === 'bot' ? (a.is_bot && a.status !== 'fechado') : (a.status === tabAtiva && !a.is_bot);
    const isAtendendoDeOutroUsuario = tabAtiva === 'em_atendimento'
      && podeVerTicketsOutrosUsuarios
      && !exibirTodos
      && user?.id
      && a.usuario_id !== user.id;
    const nomeContato = a.contato?.nome || '';
    const numeroContato = a.contato?.numero || '';
    const termoBusca = busca.toLowerCase();
    const matchBusca = busca === '' || nomeContato.toLowerCase().includes(termoBusca) || numeroContato.includes(termoBusca);
    return isTab && !isAtendendoDeOutroUsuario && matchBusca;
  });

  const handleSelecionarConversa = (at: AtendimentoTicket) => {
    setSelecionado(at);
    if (at.msgs_nao_lidas > 0) {
      const updated = { ...at, msgs_nao_lidas: 0 };
      setTickets(prev => prev.map(t => t.id === at.id ? updated : t));
      marcarTicketComoLidoLocal(at.id);
    }
    atendimentoService.marcarTicketComoLido(at.id).catch((err) => {
      console.error('Erro ao marcar atendimento como lido', err);
    });
    if (isMobile) {
      setMostrarLista(false);
      setMostrarDetalhes(false);
    }
  };

  const sincronizarTabComStatus = (status: AtendimentoTicket['status']) => {
    if (status === 'em_atendimento') {
      setTabAtiva('em_atendimento');
    } else if (status === 'pendente') {
      setTabAtiva('pendente');
    } else if (status === 'fechado_bot') {
      setTabAtiva('bot');
    }
  };

  const handleAtualizarTicket = (updatedTicket: AtendimentoTicket) => {
    setTickets(prev => prev.map(t => t.id === updatedTicket.id ? updatedTicket : t));

    if (updatedTicket.status === 'fechado') {
      setSelecionado(null);
      setMostrarLista(true);
      return;
    }

    setSelecionado(prev => {
      if (prev?.id !== updatedTicket.id) {
        return prev;
      }

      if (prev.status !== updatedTicket.status) {
        sincronizarTabComStatus(updatedTicket.status);
      }

      return updatedTicket;
    });
  };

  const handleNovoAtendimentoSuccess = (ticket: AtendimentoTicket) => {
    aplicarTicketAtualizado(ticket);
    setSelecionado(ticket);
    setTabAtiva(ticket.status === 'fechado_bot' ? 'bot' : ticket.status);
    setNovoAtendimentoAberto(false);
    if (isMobile) setMostrarLista(false);
  };

  const handleTicketReaberto = (ticket: AtendimentoTicket) => {
    aplicarTicketAtualizado(ticket);
    setSelecionado(ticket);
    setTabAtiva('em_atendimento');
    setExibirTodos(false);
    setFechadosAberto(false);
    if (isMobile) setMostrarLista(false);
  };

  const colunaLista = (
    <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0, display: 'flex', flexDirection: 'column', height: '100%', borderRight: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}>
      <Box sx={{ px: 1, py: 1, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button size="small" variant="outlined" endIcon={<ArrowDownIcon sx={{ fontSize: 14 }} />} sx={{ fontSize: '0.75rem', fontWeight: 600, borderRadius: 2, px: 1, py: 0.3, minWidth: 0, textTransform: 'none' }}>Todas</Button>
        <Box flex={1} />
        <Tooltip title="Filtros"><IconButton size="small" onClick={(e) => setFilterAnchor(e.currentTarget)}><TuneIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        {podeCriarNovoTicket && (
          <Tooltip title="Nova conversa"><IconButton size="small" color="primary" onClick={() => setNovoAtendimentoAberto(true)}><AddIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
        )}
      </Box>

      <Box sx={{ px: 1.5, py: 0.8, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f4f6f8', borderRadius: 2, px: 1.2, py: 0.3, flex: 1, minWidth: 0 }}>
            <SearchIcon sx={{ color: 'text.disabled', fontSize: 16, mr: 0.8 }} />
            <InputBase placeholder="Buscar conversa..." value={busca} onChange={(e) => setBusca(e.target.value)} sx={{ fontSize: '0.8rem', flex: 1 }} />
          </Box>
          {podeVerTicketsFechados && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ArchiveIcon sx={{ fontSize: 16 }} />}
              onClick={() => setFechadosAberto(true)}
              sx={{
                minHeight: 32,
                px: 1,
                borderRadius: 1.5,
                textTransform: 'none',
                fontSize: '0.72rem',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                '& .MuiButton-startIcon': { mr: 0.4 },
              }}
            >
              Fechados
            </Button>
          )}
        </Box>
      </Box>

      <Tabs
        value={tabAtiva}
        onChange={(_, v) => {
          setTabAtiva(v);
          setSelecionado(null);
        }}
        variant="fullWidth"
        sx={{ minHeight: 56, borderBottom: `1px solid ${theme.palette.divider}`, '& .MuiTab-root': { minHeight: 56, textTransform: 'none', py: 1, px: 1 } }}
      >
        <Tab value="em_atendimento" label={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, position: 'relative' }}>
            <HeadsetMicIcon fontSize="small" />
            <Typography variant="body2" fontWeight={600}>Atendendo</Typography>
            {contadorTab('em_atendimento') > 0 && (
              <Badge badgeContent={contadorTab('em_atendimento')} color="success" max={99} sx={{ position: 'absolute', top: 10, right: 10, '& .MuiBadge-badge': { fontSize: '0.75rem', height: 18, minWidth: 18 } }}><Box /></Badge>
            )}
          </Box>
        } />
        <Tab value="pendente" label={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, position: 'relative' }}>
            <HourglassIcon fontSize="small" />
            <Typography variant="body2" fontWeight={600}>Aguardando</Typography>
            {contadorTab('pendente') > 0 && (
              <Badge badgeContent={contadorTab('pendente')} max={99} sx={{ position: 'absolute', top: 2, right: 5, '@keyframes badgeBounce': { '0%, 100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-8px)' } }, '& .MuiBadge-badge': { width: 22, height: 22, minWidth: 22, fontSize: '0.75rem', fontWeight: 700, color: '#fff', backgroundColor: '#d32f2f', border: '2px solid #fff', borderRadius: '50%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'badgeBounce 1.4s ease-in-out infinite', boxShadow: '0 2px 6px rgba(0,0,0,0.25)' } }}><Box /></Badge>
            )}
          </Box>
        } />
        {podeExibirTicketsBot && (
          <Tab value="bot" label={
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, position: 'relative' }}>
              <BotIcon fontSize="small" />
              <Typography variant="body2" fontWeight={600}>Bot</Typography>
              {contadorTab('bot') > 0 && (
                <Badge badgeContent={contadorTab('bot')} color="secondary" max={99} sx={{ position: 'absolute', top: 10, right: -20, '& .MuiBadge-badge': { fontSize: '0.75rem', height: 18, minWidth: 18 } }}><Box /></Badge>
              )}
            </Box>
          } />
        )}
      </Tabs>

      {tabAtiva === 'em_atendimento' && (
        <Box sx={{ display: 'flex', gap: 0.75, p: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          {podeCriarNovoTicket && (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNovoAtendimentoAberto(true)}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 34,
                textTransform: 'none',
                fontSize: '0.78rem',
                fontWeight: 700,
                borderRadius: 2,
              }}
            >
              Novo
            </Button>
          )}
          {podeVerTicketsOutrosUsuarios && (
            <Button
              size="small"
              variant={exibirTodos ? 'contained' : 'outlined'}
              startIcon={<HeadsetIcon />}
              onClick={() => {
                setExibirTodos((value) => !value);
                setSelecionado(null);
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 34,
                textTransform: 'none',
                fontSize: '0.78rem',
                fontWeight: 700,
                borderRadius: 2,
                whiteSpace: 'nowrap',
              }}
            >
              {exibirTodos ? 'Meus' : 'Exibir todos'}
            </Button>
          )}
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#ddd', borderRadius: 2 } }}>
        {loading ? <LinearProgress /> : filtrados.length === 0 ? (
          <Box textAlign="center" py={6}>
            <WhatsAppIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.disabled">Nenhum atendimento</Typography>
          </Box>
        ) : (
          filtrados.map((at, i) => (
            <React.Fragment key={at.id}>
              {i > 0 && <Divider sx={{ mx: 1.5 }} />}
              <ConversaItem at={at} selecionado={selecionado?.id === at.id} onClick={() => handleSelecionarConversa(at)} />
            </React.Fragment>
          ))
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      ref={setDrawerContainer}
      sx={{
        height: 'calc(100vh - 130px)',
        display: 'flex',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 3,
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
      }}
    >
      {isMobile ? (
        mostrarLista ? colunaLista : (
          selecionado && (
            <ChatColuna
              atendimento={selecionado}
              onUpdate={handleAtualizarTicket}
              mostrarDetalhes={mostrarDetalhes}
              isMobile
              onVoltar={() => {
                setMostrarLista(true);
                setMostrarDetalhes(false);
              }}
              onToggleDetalhes={() => setMostrarDetalhes(true)}
              socket={socket}
            />
          )
        )
      ) : (
        <>
          {colunaLista}
          {selecionado ? (
            <ChatColuna atendimento={selecionado} onUpdate={handleAtualizarTicket} mostrarDetalhes={mostrarDetalhes} isMobile={false} onVoltar={() => setSelecionado(null)} onToggleDetalhes={() => setMostrarDetalhes((v) => !v)} socket={socket} />
          ) : (
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="background.default"
              flexDirection="column"
              position="relative"
              overflow="hidden"
            >
              <Box
                component="img"
                src={emptyStateBackground}
                alt=""
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: isCopa ? '78%' : '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  maxWidth: isCopa ? '72%' : '65%',
                  maxHeight: '88%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  opacity: isCopa ? 0.14 : 0.12,
                  pointerEvents: 'none',
                  zIndex: 0,
                  userSelect: 'none',
                }}
              />
              <WhatsAppIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.5, mb: 2, position: 'relative', zIndex: 1 }} />
              <Typography variant="body1" color="text.disabled" sx={{ position: 'relative', zIndex: 1 }}>
                Selecione uma conversa para iniciar o atendimento
              </Typography>
            </Box>
          )}
          {mostrarDetalhes && !isTablet && selecionado && (
            <ContatoDetalhes
              atendimento={selecionado}
              onUpdate={handleAtualizarTicket}
              onClose={() => setMostrarDetalhes(false)}
              onOpenClientProfile={handleOpenClientProfile}
            />
          )}
        </>
      )}

      <Drawer
        anchor="right"
        open={isMobile && mostrarDetalhes && Boolean(selecionado)}
        onClose={() => setMostrarDetalhes(false)}
        container={drawerContainer}
        ModalProps={{
          container: drawerContainer,
          style: { position: 'absolute' },
        }}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 360,
            height: '100%',
            position: 'absolute',
            top: 0,
          },
        }}
        sx={{
          position: 'absolute',
          zIndex: (t) => t.zIndex.drawer,
          '& .MuiBackdrop-root': {
            position: 'absolute',
          },
        }}
      >
        {selecionado && (
          <ContatoDetalhes
            atendimento={selecionado}
            onUpdate={handleAtualizarTicket}
            onClose={() => setMostrarDetalhes(false)}
            fullWidth
            onOpenClientProfile={handleOpenClientProfile}
          />
        )}
      </Drawer>

      <Menu anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={() => setFilterAnchor(null)} PaperProps={{ sx: { borderRadius: 2, minWidth: 160 } }}>
        {['Todos', 'Não lidos', 'Atribuídos a mim', 'Sem atendente'].map((f) => (
          <MenuItem key={f} onClick={() => setFilterAnchor(null)} sx={{ fontSize: '0.8rem' }}>{f}</MenuItem>
        ))}
      </Menu>

      <AtendimentosFechadosDialog
        open={fechadosAberto && podeVerTicketsFechados}
        onClose={() => setFechadosAberto(false)}
        onTicketReopened={handleTicketReaberto}
      />

      <NovoAtendimentoDialog
        open={novoAtendimentoAberto && podeCriarNovoTicket}
        onClose={() => setNovoAtendimentoAberto(false)}
        onSuccess={handleNovoAtendimentoSuccess}
      />

      {perfilClienteModals.map((modal) => {
        const minimizedStackIndex = perfilClienteModals
          .filter((item) => item.minimized)
          .findIndex((item) => item.instanceId === modal.instanceId);
        const modalZIndex = modal.minimized
          ? theme.zIndex.modal + minimizedStackIndex + 1
          : theme.zIndex.modal + 1000 + perfilClienteModals.length;

        return (
          <ClientDetailsModal
            key={modal.instanceId}
            clientId={modal.clientId}
            clientName={modal.clientName}
            minimized={modal.minimized}
            onMinimizedChange={(minimized) => handleClientProfileMinimizedChange(modal.instanceId, minimized)}
            onClose={() => handleCloseClientProfile(modal.instanceId)}
            stackIndex={minimizedStackIndex >= 0 ? minimizedStackIndex : 0}
            zIndex={modalZIndex}
          />
        );
      })}
    </Box>
  );
};

export default AtendimentosPage;
